/**
 * The Profile Document — WINR Studio's customization unit for a user's public
 * identity. Same philosophy as ThemeDoc: versioned JSONB, validated on read,
 * compiled into concrete styles consumed by the REAL profile surfaces
 * (ProfilePreviewCard, UserProfileModal, FriendshipInfoRail). Every field here
 * is wired into production surfaces — nothing decorative.
 */
import type { CSSProperties } from 'react';
import { migrateBannerDesign, type BannerDesign } from './bannerLayers';

export const PROFILE_DOC_VERSION = 1;

export type AvatarFrame = 'none' | 'accent' | 'glow' | 'gradient';
export type ProfileNameStyle = 'plain' | 'gradient';

export interface ProfileDoc {
  version: typeof PROFILE_DOC_VERSION;
  /** Layered banner design; null = fall back to the equipped title card art.
   *  `focal` (0–100 percentages) reframes the design inside each surface crop. */
  banner: { design: BannerDesign | null; focal: { x: number; y: number } };
  /** Identity accent (avatar ring, handle, name gradient); null = emblem glow default. */
  accent: string | null;
  frame: AvatarFrame;
  nameStyle: ProfileNameStyle;
  showEmblem: boolean;
}

export function defaultProfileDoc(): ProfileDoc {
  return {
    version: PROFILE_DOC_VERSION,
    banner: { design: null, focal: { x: 50, y: 50 } },
    accent: null,
    frame: 'accent',
    nameStyle: 'plain',
    showEmblem: true,
  };
}

export function migrateProfileDoc(raw: unknown): ProfileDoc {
  const base = defaultProfileDoc();
  if (!raw || typeof raw !== 'object') return base;
  const d = raw as Partial<ProfileDoc> & Record<string, unknown>;
  const rawBanner = d.banner as { design?: unknown; focal?: { x?: unknown; y?: unknown } } | undefined;
  const focal =
    typeof rawBanner?.focal?.x === 'number' && typeof rawBanner.focal.y === 'number'
      ? {
          x: Math.min(100, Math.max(0, rawBanner.focal.x)),
          y: Math.min(100, Math.max(0, rawBanner.focal.y)),
        }
      : { x: 50, y: 50 };
  return {
    version: PROFILE_DOC_VERSION,
    banner: { design: migrateBannerDesign(rawBanner?.design), focal },
    accent:
      typeof d.accent === 'string' && /^#[0-9a-fA-F]{3,8}$/.test(d.accent) ? d.accent : null,
    frame: (['none', 'accent', 'glow', 'gradient'] as const).includes(d.frame as AvatarFrame)
      ? (d.frame as AvatarFrame)
      : base.frame,
    nameStyle: d.nameStyle === 'gradient' ? 'gradient' : 'plain',
    showEmblem: d.showEmblem !== false,
  };
}

/** Resolved styling for a profile surface (doc + the user's cosmetic fallbacks). */
export interface CompiledProfileTheme {
  doc: ProfileDoc;
  /** Effective accent (doc accent, else the fallback the caller derives from cosmetics). */
  accent: string;
  hasCustomBanner: boolean;
}

export function compileProfileTheme(doc: ProfileDoc, fallbackAccent: string): CompiledProfileTheme {
  return {
    doc,
    accent: doc.accent ?? fallbackAccent,
    hasCustomBanner: Boolean(doc.banner.design && doc.banner.design.layers.length > 0),
  };
}

/** Avatar ring styling per frame choice — shared by every surface so they match. */
export function avatarFrameStyle(frame: AvatarFrame, accent: string): CSSProperties {
  switch (frame) {
    case 'none':
      return { borderColor: 'rgba(255,255,255,0.14)' };
    case 'glow':
      return { borderColor: accent, boxShadow: `0 0 18px ${accent}aa, 0 0 2px ${accent}` };
    case 'gradient':
      return {
        borderColor: 'transparent',
        backgroundImage: `linear-gradient(#101016,#101016), linear-gradient(135deg, ${accent}, #ffffff66)`,
        backgroundOrigin: 'border-box',
        backgroundClip: 'padding-box, border-box',
      };
    default:
      return { borderColor: accent };
  }
}
